import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchPeriodWeeks } from '../../../../api/periodWeeksApi';
import type { RootState } from '../../../../store';
import type { AdminLocation } from '../../../../store/slices/adminLocationsSlice';
import type { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import { useAdminLocations } from '../../../AnalyticsHub/hooks/useAdminLocations';
import { WES_FIRST_ANALYTICS_YEAR, WES_PERIODS_PER_YEAR, WES_WEEKS_PER_PERIOD } from './wesPalletStagingStaticData';

// Filter option sources and values copied from CrewingAnalyticsDashboard / PerfectTruckAnalyticsDashboard.

export interface WesCurrentPeriodWeek {
  year: number;
  period: number;
  // Week within the period, as stored in appData.periodWeek.
  week: number;
}

export interface WesReference {
  locations: AdminLocation[];
  currentPeriodWeek: WesCurrentPeriodWeek | null;
  // Raw GET /period-weeks data for the selected years; empty means the 4-weeks-per-period fallback is used.
  periodWeeks: any[];
}

export type WesWeekItem = KPIFilterDropdownItem & { period: number; weekInYear: number };

export const normalizeFilterValue = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const sortByText = (a: KPIFilterDropdownItem, b: KPIFilterDropdownItem) => a.displayText.localeCompare(b.displayText);

export const regionOptions = (locations: AdminLocation[]): KPIFilterDropdownItem[] => {
  const seen = new Set<string>();
  const options: KPIFilterDropdownItem[] = [];
  locations.forEach((loc) => {
    const r = loc.region?.trim();
    if (r && !seen.has(r)) {
      seen.add(r);
      options.push({ id: r, displayText: r });
    }
  });
  return options.sort(sortByText);
};

export const marketOptions = (locations: AdminLocation[], regions: string[]): KPIFilterDropdownItem[] => {
  const seen = new Set<string>();
  const options: KPIFilterDropdownItem[] = [];
  locations
    .filter((loc) => regions.length === 0 || regions.some((r) => normalizeFilterValue(r) === normalizeFilterValue(loc.region)))
    .forEach((loc) => {
      const m = loc.market?.trim();
      if (m && !seen.has(m)) {
        seen.add(m);
        options.push({ id: m, displayText: m });
      }
    });
  return options.sort(sortByText);
};

export const locationOptions = (locations: AdminLocation[], regions: string[], markets: string[]): KPIFilterDropdownItem[] =>
  locations
    .filter((loc) => {
      const matchesRegion = regions.length === 0 || regions.some((r) => normalizeFilterValue(r) === normalizeFilterValue(loc.region));
      const matchesMarket = markets.length === 0 || markets.some((m) => normalizeFilterValue(m) === normalizeFilterValue(loc.market));
      return matchesRegion && matchesMarket;
    })
    .map((loc) => ({ id: loc.locId, displayText: loc.locName }))
    .sort(sortByText);

// Current year back to the first analytics year, newest first.
export const yearOptions = (currentYear: number): KPIFilterDropdownItem[] => {
  const count = Math.max(1, currentYear - WES_FIRST_ANALYTICS_YEAR + 1);
  return Array.from({ length: count }, (_, i) => ({ id: String(currentYear - i), displayText: String(currentYear - i) }));
};

export const periodOptions = (): KPIFilterDropdownItem[] =>
  Array.from({ length: WES_PERIODS_PER_YEAR }, (_, i) => ({ id: `P${i + 1}`, displayText: `P${i + 1}` }));

// Real weekInYear numbers from /period-weeks when available; otherwise the fixed 4-weeks-per-period formula.
// The id is period + week-in-period based so quick selects can build it without waiting for the API.
export const weekOptions = (periodWeeks: any[], selectedPeriods: string[]): WesWeekItem[] => {
  const apiWeeksByPeriod = new Map<number, { weekInYear: number; weekInPeriod: number }[]>();
  periodWeeks.forEach((yd: any) => {
    (yd.periods ?? []).forEach((pd: any) => {
      const periodNum = Number(pd.period);
      const weeks = (pd.weeks ?? []).map((week: any, idx: number) => ({
        weekInYear: typeof week === 'number' ? week : Number(week?.weekInYear ?? week?.week ?? idx + 1),
        weekInPeriod: idx + 1,
      }));
      if (weeks.length > 0) apiWeeksByPeriod.set(periodNum, weeks);
    });
  });

  const items: WesWeekItem[] = [];
  Array.from({ length: WES_PERIODS_PER_YEAR }, (_, i) => i + 1).forEach((periodNum) => {
    const weeks =
      apiWeeksByPeriod.get(periodNum) ??
      Array.from({ length: WES_WEEKS_PER_PERIOD }, (_, idx) => ({
        weekInYear: (periodNum - 1) * WES_WEEKS_PER_PERIOD + idx + 1,
        weekInPeriod: idx + 1,
      }));
    weeks.forEach(({ weekInYear, weekInPeriod }) => {
      items.push({
        id: `P${periodNum}W${weekInPeriod}`,
        displayText: `W${weekInYear} (P${periodNum}W${weekInPeriod})`,
        period: periodNum,
        weekInYear,
      });
    });
  });
  const unique = new Map<string, WesWeekItem>();
  items.forEach((it) => {
    if (!unique.has(String(it.id))) unique.set(String(it.id), it);
  });
  return Array.from(unique.values())
    .filter((it) => selectedPeriods.length === 0 || selectedPeriods.includes(`P${it.period}`))
    .sort((a, b) => a.weekInYear - b.weekInYear);
};

// Reads the same reference data Crewing does: admin locations, the current period/week, and /period-weeks for the selected years.
export function useWesFilterReference(selectedYears: string[]): WesReference {
  const { locations } = useAdminLocations();
  const current = useSelector((state: RootState) => state.appData?.periodWeek?.data);
  const [periodWeeks, setPeriodWeeks] = useState<any[]>([]);
  const yearsKey = selectedYears.join(',');

  useEffect(() => {
    const years = selectedYears.map(Number).filter((n) => !Number.isNaN(n));
    if (years.length === 0) {
      setPeriodWeeks([]);
      return undefined;
    }
    let isMounted = true;
    fetchPeriodWeeks(years)
      .then((res) => {
        if (isMounted) setPeriodWeeks(res?.data ?? []);
      })
      .catch(() => {
        if (isMounted) setPeriodWeeks([]);
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsKey]);

  const currentPeriodWeek = useMemo<WesCurrentPeriodWeek | null>(
    () => (current?.year && current?.period && current?.week ? { year: current.year, period: current.period, week: current.week } : null),
    [current?.year, current?.period, current?.week],
  );

  return useMemo(() => ({ locations, currentPeriodWeek, periodWeeks }), [locations, currentPeriodWeek, periodWeeks]);
}
